import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
} from '@mui/material';

const ENTITY_TYPES = ['ownership', 'facility'];

const DATA_TYPES = [
  'string',
  'integer',
  'decimal',
  'boolean',
  'date',
  'datetime',
  'json',
];

function AttributeDefinitionDialog({ open, onClose, onSubmit, editData }) {
  const [formValues, setFormValues] = useState({
    entityType: '',
    name: '',
    label: '',
    dataType: '',
    defaultValue: '',
    isRequired: false,
    shareValues: false,
    visible: true,
  });

  const [errors, setErrors] = useState({});

  // Populate form when dialog opens in edit mode
  useEffect(() => {
    if (open) {
      if (editData) {
        // Editing mode
        setFormValues({
          id: editData.id,
          entityType: editData.entityType || '',
          name: editData.name || '',
          label: editData.label || '',
          dataType: editData.dataType || '',
          defaultValue: editData.defaultValue || '',
          isRequired: !!editData.isRequired,
          shareValues: !!editData.shareValues,
          visible: !!editData.visible,
        });
      } else {
        // Creating mode — clear form
        setFormValues({
          entityType: '',
          name: '',
          label: '',
          dataType: '',
          defaultValue: '',
          isRequired: false,
        });
      }
    }
  }, [open, editData]);

  const handleChange = (field) => (e) => {
    const value =
      e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    setFormValues((prev) => ({ ...prev, [field]: value }));
  };

  const handleSave = () => {
    const newErrors = {};
    if (!formValues.entityType) newErrors.entityType = 'Entity type is required';
    if (!formValues.name) newErrors.name = 'Name is required';
    if (!formValues.label) newErrors.label = 'Label is required';
    if (!formValues.dataType) newErrors.dataType = 'Data type is required';
    setErrors(newErrors);
    if (Object.keys(newErrors).length > 0) return;

    // Pass mode flag + values back to parent
    onSubmit(formValues, !!editData);

    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth>
      <DialogTitle>
        {editData ? 'Edit Attribute Definition' : 'Add Attribute Definition'}
      </DialogTitle>
      <DialogContent>
        <FormControl fullWidth margin="normal">
          <InputLabel>Entity Type</InputLabel>
          <Select
            value={formValues.entityType}
            onChange={handleChange('entityType')}
            error={!!errors.entityType}
          >
            {ENTITY_TYPES.map((type) => (
              <MenuItem value={type} key={type}>
                {type}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <TextField
          margin="normal"
          fullWidth
          label="Name"
          value={formValues.name}
          onChange={handleChange('name')}
          error={!!errors.name}
          helperText={errors.name}
        />

        <TextField
          margin="normal"
          fullWidth
          label="Label"
          value={formValues.label}
          onChange={handleChange('label')}
          error={!!errors.label}
          helperText={errors.label}
        />

        <FormControl fullWidth margin="normal">
          <InputLabel>Data Type</InputLabel>
          <Select
            value={formValues.dataType}
            onChange={handleChange('dataType')}
            error={!!errors.dataType}
          >
            {DATA_TYPES.map((type) => (
              <MenuItem value={type} key={type}>
                {type}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <TextField
          margin="normal"
          fullWidth
          label="Default Value"
          value={formValues.defaultValue}
          onChange={handleChange('defaultValue')}
        />

        <div style={{ marginTop: '1rem' }}>
          <label>
            <input
              type="checkbox"
              checked={formValues.isRequired}
              onChange={handleChange('isRequired')}
            />{' '}
            Required
          </label>
        </div>

        <div style={{ marginTop: '1rem' }}>
          <label>
            <input
              type="checkbox"
              checked={formValues.shareValues}
              onChange={handleChange('shareValues')}
            />{' '}
            Share Values
          </label>
        </div>

        <div style={{ marginTop: '1rem' }}>
          <label>
            <input
              type="checkbox"
              checked={formValues.visible}
              onChange={handleChange('visible')}
            />{' '}
            Visible
          </label>
        </div>

      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} color="secondary">
          Cancel
        </Button>
        <Button onClick={handleSave} variant="contained" color="primary">
          {editData ? 'Update' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default AttributeDefinitionDialog;